/**
 * useMerlinFollowups — the 30s poll behind the Pending tab (kanban 43160d8b):
 * every follow-up Merlin promised the reader, open and settled, so the tab's
 * count, its groups and the "Merlin will check back on…" line on an item all
 * read one list. Owned by the Quality Triage shell (one poll for the door,
 * like useQcpDataRequests); pages read it from the Outlet context and never
 * start their own.
 *
 * Goes dark when the deploy has no follow-ups API (404) or the reader has no
 * grant for it (403): the tab and the lines then stay hidden instead of
 * showing an empty promise list.
 */
import { useCallback, useEffect, useState } from 'react';

import { listFollowups, type MerlinFollowupRead } from '../../services/merlinFollowups';

const POLL_INTERVAL_MS = 30000; // matches the door's other polls

export interface MerlinFollowupsState {
  rows: MerlinFollowupRead[];
  /** False when the API is absent (404) or forbidden (403) on this deploy. */
  available: boolean;
  /** True once the first answer (or refusal) has landed. */
  loaded: boolean;
  refresh: () => void;
  /** Fold a mutated row (cancel / check now) into the list at once. */
  applyUpdate: (updated: MerlinFollowupRead) => void;
}

export function useMerlinFollowups(enabled: boolean): MerlinFollowupsState {
  const [rows, setRows] = useState<MerlinFollowupRead[]>([]);
  const [available, setAvailable] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(() => {
    listFollowups()
      .then((data) => {
        setAvailable(true);
        setRows(data);
        setLoaded(true);
      })
      .catch((error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 403) {
          setAvailable(false);
          setLoaded(true);
        }
        // otherwise: polling is best-effort; keep what we have
      });
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, refresh]);

  const applyUpdate = useCallback((updated: MerlinFollowupRead) => {
    setRows((prev) =>
      prev.some((r) => r.id === updated.id)
        ? prev.map((r) => (r.id === updated.id ? updated : r))
        : [updated, ...prev],
    );
  }, []);

  return { rows, available, loaded, refresh, applyUpdate };
}
